import { useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import { applyFilters, setPeriod } from '@/modules/analytics/redux/actions';
import {
  makeSelectEndDate,
  makeSelectFiltersByPageType,
  makeSelectStartDate,
  selectAnalyticsFiltersLoaded,
} from '@/modules/analytics/redux/selectors';
import { AnalyticsType } from '@/shared/types/analytics';
import { urlSafeBase64Decode, urlSafeBase64Encode } from '@/util/util';

const useFilters = (type: AnalyticsType) => {
  const navigate = useNavigate();
  const [dateOpen, setDateOpen] = useState(false);
  const [internalFilterOpen, setFilterOpen] = useState(false);
  const filterOpen = !dateOpen && internalFilterOpen;
  const dispatch = useDispatch();

  const filterPage = 'analytics' as const;

  const startDateSelector = useMemo(() => makeSelectStartDate(filterPage), [filterPage]);
  const endDateSelector = useMemo(() => makeSelectEndDate(filterPage), [filterPage]);
  const startDate = useSelector(startDateSelector);
  const endDate = useSelector(endDateSelector);
  const dateFilters = useMemo(() => ({ startDate, endDate }), [endDate, startDate]);
  const filtersSelector = useMemo(
    () => makeSelectFiltersByPageType(filterPage, type),
    [filterPage, type]
  );
  const filters = useSelector(filtersSelector, shallowEqual);
  const filtersLoaded = useSelector(selectAnalyticsFiltersLoaded);
  const [urlParams] = useSearchParams();
  const isReplay = urlParams.get('session_id');

  const filterParam = useMemo(
    () => (urlParams.get('filters') ? urlSafeBase64Decode(urlParams.get('filters')) : ''),
    [urlParams]
  );
  const dateParam = useMemo(
    () => (urlParams.get('date') ? urlSafeBase64Decode(urlParams.get('date')) : ''),
    [urlParams]
  );

  useEffect(() => {
    if (dateParam) {
      try {
        const { startDate: urlStart, endDate: urlEnd } = JSON.parse(dateParam);
        if (urlStart !== dateFilters.startDate || urlEnd !== dateFilters.endDate) {
          dispatch(setPeriod({ startDate: urlStart, endDate: urlEnd }));
        }
      } catch (error) {
        console.error('Failed to parse date parameters:', error);
        // Keep existing date filters if parsing fails
      }
    }
  }, [dateParam, dateFilters, dispatch]);

  useEffect(() => {
    if (type !== AnalyticsType.ACCOUNT) {
      return;
    }

    if (filtersLoaded) {
      return;
    }

    const nextFilters = Array.isArray(filters) ? filters : [];
    dispatch(applyFilters({ type, filters: nextFilters }));
  }, [dispatch, filters, filtersLoaded, type]);

  useEffect(() => {
    if (!filterParam && !dateParam && !isReplay) {
      const dateQuery = !isEmpty(dateFilters)
        ? `date=${urlSafeBase64Encode(JSON.stringify(dateFilters))}`
        : '';

      navigate({ ...window.location, search: dateQuery }, { replace: true });
    }
  }, [dateFilters, dateParam, filterParam, isReplay, navigate]);

  return { dateOpen, setDateOpen, filterOpen, setFilterOpen };
};

export default useFilters;
